import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import SocialAuthButtons from '@/components/SocialAuthButtons';
import { fetchIdentityConfig, linkIdentity, listIdentities, unlinkIdentity } from '@/lib/social-auth';

const LABELS = { google: 'Google', apple: 'Apple' };

function formatLinkedAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function ConnectedAccountsPanel() {
  const [identities, setIdentities] = useState([]);
  const [hasPassword, setHasPassword] = useState(true);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [configuredProviders, setConfiguredProviders] = useState(['google', 'apple']);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [data, config] = await Promise.all([listIdentities(), fetchIdentityConfig().catch(() => null)]);
      const rows = Array.isArray(data.identities) ? data.identities : [];
      setIdentities(rows);
      setHasPassword(Boolean(data.hasPassword));
      const providers = Array.isArray(config?.providers) ? config.providers : [];
      const linked = new Set(rows.map((i) => i.provider));
      setConfiguredProviders([...new Set([...providers, ...linked])]);
    } catch (err) {
      toast.error('Could not load connected accounts', { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const onProviderToken = async (provider, idToken) => {
    setLinking(true);
    try {
      await linkIdentity({ provider, idToken });
      toast.success(`${LABELS[provider] || provider} connected`);
      await reload();
    } catch (err) {
      toast.error('Could not link account', { description: err?.message });
    } finally {
      setLinking(false);
    }
  };

  const onUnlink = async (id) => {
    try {
      await unlinkIdentity(id);
      toast.success('Disconnected');
      await reload();
    } catch (err) {
      toast.error('Could not disconnect', { description: err?.message });
    }
  };

  const linkedProviders = new Set(identities.map((i) => i.provider));

  return (
    <div className="surface w-full p-6 space-y-5">
      <div>
        <div className="font-medium">Connected accounts</div>
        <p className="text-xs text-[var(--text-tertiary)] mt-1 max-w-prose">
          Link Google or Apple to sign in without your password. Manage bindings here for workspace and platform
          operators.
        </p>
      </div>
      {loading ? (
        <p className="text-sm text-[var(--text-secondary)]">Loading…</p>
      ) : (
        <ul className="w-full divide-y divide-[var(--border-default)]">
          {configuredProviders.map((provider) => {
            const row = identities.find((i) => i.provider === provider);
            return (
              <li
                key={provider}
                className="flex w-full items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
              >
                <span className="text-sm font-medium shrink-0">{LABELS[provider] || provider}</span>
                {row ? (
                  <div className="flex items-center justify-end gap-2 shrink-0">
                    <span className="text-xs font-mono text-[var(--text-tertiary)]">
                      Connected{row.linkedAt ? ` · ${formatLinkedAt(row.linkedAt)}` : ''}
                    </span>
                    <button
                      type="button"
                      className="btn-ghost text-xs cursor-pointer"
                      onClick={() => onUnlink(row.id)}
                      disabled={!hasPassword && identities.length <= 1}
                      title={
                        !hasPassword && identities.length <= 1
                          ? 'Set a password before removing your only sign-in method'
                          : 'Disconnect'
                      }
                    >
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-[var(--text-tertiary)] shrink-0">Not connected</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!loading && linkedProviders.size < 2 ? (
        <div className={`w-full pt-1 ${linking ? 'opacity-60 pointer-events-none' : ''}`}>
          <p className="text-overline mb-3">Connect another sign-in method</p>
          <SocialAuthButtons
            mode="link"
            className="w-full"
            onProviderToken={onProviderToken}
            disabled={linking}
          />
        </div>
      ) : null}
      {!hasPassword ? (
        <p className="text-xs text-[var(--text-secondary)]">
          You sign in with a linked provider only. Set a password under Security → password reset before disconnecting
          your last provider.
        </p>
      ) : null}
    </div>
  );
}
